import React, { useId } from 'react';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';

export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonPreset = 'primary' | 'white' | 'danger' | 'danger-light' | 'blue' | 'custom';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  Component?: React.ElementType;
  href?: string;
  target?: string;
  size?: ButtonSize;
  preset?: ButtonPreset | 'custom';
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  onPress?: () => void;
  disabled?: boolean;
  tooltip?: string;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg'
};

const presetClasses: Record<ButtonPreset, string> = {
  custom: '',
  primary: 'bg-gradient-to-tr from-yellow-500 to-yellow-200 text-black hover:from-yellow-400 hover:to-yellow-100 active:from-yellow-600 active:to-yellow-300',
  white: 'bg-gradient-to-tr from-gray-200 to-white text-black border border-gray-300 hover:from-gray-100 hover:to-white active:from-gray-100 active:to-gray-200',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  'danger-light': 'bg-red-100 text-red-600 hover:bg-red-200 active:bg-red-300',
  blue: 'bg-gradient-to-tr from-blue-700 to-blue-400 text-white hover:from-blue-600 hover:to-blue-300 active:from-blue-600 active:to-blue-300'
};

export const Button: React.FC<ButtonProps> = ({
  Component = 'button',
  size = 'md',
  preset = 'primary',
  label,
  className,
  style,
  onPress,
  children,
  disabled,
  tooltip,
  ...props
}) => {
  const buttonId = useId();
  const baseClasses = 'flex flex-row items-center rounded-xl font-bold transition-colors duration-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 whitespace-nowrap flex flex-row gap-1';
  
  const combinedClasses = cx(
    baseClasses,
    sizeClasses[size],
    presetClasses[preset] || '',
    disabled && 'opacity-50 cursor-not-allowed',
    !disabled && 'cursor-pointer',
    className
  );

  return (<>
    <Component
      className={combinedClasses}
      style={style}
      onClick={onPress}
      disabled={disabled}
      data-tooltip-content={tooltip}
      data-tooltip-id={buttonId}
      {...props}
    >
      {label || children}
    </Component>
    {tooltip && (
      <Tooltip id={buttonId} />
    )}
  </>);
};

export default Button;
